import { useEffect, useRef } from 'react';
import { useRouter, useRootNavigationState } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { isTransientServerError, useApiClient } from '../../../helpers/apiClient.helper';
import { joinWorldCupCompetitionGroup } from '../../worldcup/helpers/worldCupCompetitionGroupsApi';
import { worldCupQueryKeys } from '../../worldcup/constants/worldcupQueryKeys';
import { competitionBasePath, competitionGroupRoute } from '../../competitions/helpers/competitionRoutes.helper';
import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants';
import {
  clearPendingWcSquadJoinCode,
  getPendingSquadJoin,
} from '../helpers/pendingWcSquadJoin.helper';
import { logger } from '../../helpers/logger.helper';

/** Navigation stability delay — matches useDeepLinkHandler / usePendingB2bJoinHandler. */
const NAV_STABILITY_DELAY_MS = 300;
/** Retry once to cover the first-login ensureUserExists settling window. */
const MAX_JOIN_RETRIES = 1;
const JOIN_RETRY_DELAY_MS = 4000;

/**
 * Completes a pending World Cup squad join after sign-in when OAuth or post-auth
 * routing lands on `/app/` instead of the auto-join screen. The squad-join intent
 * is carried across the Google OAuth hop only in sessionStorage, which is lost
 * when the invite link was opened in an in-app browser (WhatsApp/Instagram) and
 * OAuth completes in a different browser context — leaving the user signed in with
 * no squad and no error. The code is persisted durably before OAuth
 * (savePendingWcSquadJoinFromReturnPath); this hook fires it from the app shell.
 */
export function usePendingWcSquadJoinHandler(): void {
  const { isLoaded, isSignedIn, isAnonymous } = useSupabaseAuth();
  const rootNavState = useRootNavigationState();
  const apiClient = useApiClient();
  const router = useRouter();
  const queryClient = useQueryClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const inFlightRef = useRef(false);
  // Tracks the in-flight retry timeout so unmount can cancel it (otherwise a pending
  // retry fires after the component is gone and touches a dead navigator).
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || isAnonymous) return;
    if (!rootNavState?.key) return;
    if (inFlightRef.current) return;

    const timer = setTimeout(() => {
      void (async () => {
        const pending = await getPendingSquadJoin();
        const inviteCode = pending?.inviteCode ?? null;
        const competitionId = pending?.competitionId ?? WORLD_CUP_COMPETITION_ID;
        if (!inviteCode) return;

        inFlightRef.current = true;

        /** Attempt the join; retries once on transient 5xx to cover the first-login race. */
        const doJoin = async (attempt: number): Promise<void> => {
          try {
            const { groupId } = await joinWorldCupCompetitionGroup(apiClientRef.current, competitionId, inviteCode);
            await clearPendingWcSquadJoinCode();
            void queryClient.invalidateQueries({
              queryKey: worldCupQueryKeys.groups(competitionId),
            });
            router.replace(competitionGroupRoute(competitionBasePath(competitionId), groupId) as never);
            // inFlightRef intentionally left true — navigation underway.
          } catch (err) {
            if (isTransientServerError(err) && attempt < MAX_JOIN_RETRIES) {
              logger.warn('[usePendingWcSquadJoinHandler] retrying join after 5xx', {
                attempt: attempt + 1,
              });
              retryTimerRef.current = setTimeout(() => {
                retryTimerRef.current = null;
                void doJoin(attempt + 1);
              }, JOIN_RETRY_DELAY_MS);
              return; // keep inFlightRef.current = true during retry window
            }
            // Terminal failures (already a member, invalid/expired code) must clear the
            // pending code so we don't re-attempt on every app open. Transient 5xx keep
            // it for the next launch.
            if (!isTransientServerError(err)) {
              await clearPendingWcSquadJoinCode();
            }
            logger.warn('[usePendingWcSquadJoinHandler] join failed', {
              message: err instanceof Error ? err.message : String(err),
            });
            inFlightRef.current = false;
          }
        };

        void doJoin(0);
      })();
    }, NAV_STABILITY_DELAY_MS);

    return () => {
      clearTimeout(timer);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [isLoaded, isSignedIn, isAnonymous, rootNavState?.key, router, queryClient]);
}
